import { useMutation, useQueryClient } from '@tanstack/react-query'
import { BadgeCheck, Download, Flame, Sparkles, UserCheck, X } from 'lucide-react'
import { useState } from 'react'

import { annotationKindOf, type AssetCard as AssetCardData } from '@/api/assets'
import { fetchJson, isApiError, path } from '@/api/client'
import { startMediaWarm } from '@/api/jobs'
import { keys } from '@/api/keys'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/toast'
import { cn } from '@/components/ui/utils'
import { plural } from '@/lib/format'
import { useLibraryStore } from '@/stores/libraryStore'

import { AutoAnnotateDialog } from './AutoAnnotateDialog'

/**
 * The bar that appears once something is selected: the five things an
 * operator does to a batch.
 *
 * `publish` and `mark reviewed` are **per-asset** routes (§5.3) — there is no
 * bulk endpoint — so they run here through a small bounded pool and report
 * an honest tally: how many went through, how many the server refused and
 * why. A refusal is almost always "this one still has errors to fix", which
 * is exactly what the operator needs to hear.
 */

export interface BulkActionBarProps {
  /** The full rows of the current page, so the bar can tell audio from art. */
  items: AssetCardData[]
  className?: string
}

/** Concurrency for the per-uid fan-outs — polite to a single-worker API. */
const POOL = 4

interface BatchResult {
  ok: string[]
  failed: { uid: string; message: string }[]
}

async function runBatch(uids: string[], fn: (uid: string) => Promise<unknown>): Promise<BatchResult> {
  const result: BatchResult = { ok: [], failed: [] }
  const queue = [...uids]
  const workers = Array.from({ length: Math.min(POOL, queue.length) }, async () => {
    for (; ;) {
      const uid = queue.shift()
      if (uid === undefined) return
      try {
        await fn(uid)
        result.ok.push(uid)
      } catch (err) {
        result.failed.push({ uid, message: isApiError(err) ? err.message : err instanceof Error ? err.message : String(err) })
      }
    }
  })
  await Promise.all(workers)
  return result
}

function report(verb: string, r: BatchResult) {
  if (r.failed.length === 0) {
    toast.success(`${verb} ${r.ok.length} ${plural(r.ok.length, 'asset')}`)
    return
  }
  const first = r.failed[0]
  toast.warning(`${verb} ${r.ok.length} of ${r.ok.length + r.failed.length}`, {
    description: `${r.failed.length} refused. First: ${first?.message ?? 'unknown reason'}`,
  })
}

export function BulkActionBar({ items, className }: BulkActionBarProps) {
  const qc = useQueryClient()
  const selected = useLibraryStore((s) => s.selected)
  const clearSelection = useLibraryStore((s) => s.clearSelection)
  const [aiOpen, setAiOpen] = useState(false)

  const uids = [...selected]
  const rows = items.filter((a) => selected.has(a.uid))
  // Audio has no annotation kind (§5.3), so it is excluded from the three
  // annotation actions instead of being sent and refused one by one.
  const annotatable = (rows.length > 0 ? rows : []).filter((a) => annotationKindOf(a.kind) !== null).map((a) => a.uid)
  const annotatableUids = rows.length > 0 ? annotatable : uids

  const afterAnnotationChange = () => {
    void qc.invalidateQueries({ queryKey: keys.assets.all() })
    void qc.invalidateQueries({ queryKey: keys.annotation.all() })
  }

  const publish = useMutation({
    mutationFn: () => runBatch(annotatableUids, (uid) => fetchJson(path`/api/annotations/${uid}/publish`, { method: 'POST', body: { tier: 'reviewed' } })),
    onSuccess: (r) => {
      report('Published', r)
      afterAnnotationChange()
    },
  })

  const markReviewed = useMutation({
    mutationFn: () => runBatch(annotatableUids, (uid) => fetchJson(path`/api/annotations/${uid}/status`, { method: 'POST', body: { status: 'reviewed' } })),
    onSuccess: (r) => {
      report('Marked reviewed', r)
      afterAnnotationChange()
    },
  })

  const warm = useMutation({
    mutationFn: () => startMediaWarm({ uids, variants: ['thumb', 'preview', 'poster', 'raster', 'char_thumb'] }),
    onSuccess: (res) => {
      toast.success('Preparing pictures', { description: `${res.planned.count} ${plural(res.planned.count, 'file')} queued.` })
      void qc.invalidateQueries({ queryKey: keys.jobs.all() })
    },
    onError: (e) => toast.error('Could not start warming', { description: e instanceof Error ? e.message : String(e) }),
  })

  /** Export the selection as a uid list the operator can paste or keep. */
  const exportSelection = () => {
    const payload = {
      exported_at: new Date().toISOString(),
      count: uids.length,
      assets: (rows.length > 0 ? rows : []).map((a) => ({
        uid: a.uid,
        kind: a.kind,
        name: a.name,
        status: a.status,
        review: a.review.status,
        published_tier: a.review.published_tier,
      })),
      uids,
    }
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `story-studio-selection-${uids.length}.json`
    a.click()
    URL.revokeObjectURL(url)
    toast.success(`Saved a list of ${uids.length} ${plural(uids.length, 'asset')}`)
  }

  if (uids.length === 0) return null
  const busy = publish.isPending || markReviewed.isPending || warm.isPending

  return (
    <>
      <div
        role="toolbar"
        aria-label="Actions for the selected assets"
        data-testid="bulk-action-bar"
        className={cn(
          'sticky bottom-12 z-20 mx-auto flex w-fit max-w-full flex-wrap items-center gap-2 rounded-2xl border border-line',
          'bg-paper/95 px-3 py-2 shadow-pop backdrop-blur',
          className,
        )}
      >
        <span className="px-1 text-sm font-semibold text-ink">
          {uids.length} selected
        </span>

        <Button size="sm" variant="primary" onClick={() => setAiOpen(true)} disabled={annotatableUids.length === 0 || busy}>
          <Sparkles aria-hidden />
          Let Claude look
        </Button>
        <Button size="sm" onClick={() => markReviewed.mutate()} loading={markReviewed.isPending} disabled={annotatableUids.length === 0 || busy}>
          <UserCheck aria-hidden />
          Mark checked
        </Button>
        <Button size="sm" onClick={() => publish.mutate()} loading={publish.isPending} disabled={annotatableUids.length === 0 || busy}>
          <BadgeCheck aria-hidden />
          Publish
        </Button>
        <Button size="sm" variant="ghost" onClick={() => warm.mutate()} loading={warm.isPending} disabled={busy}>
          <Flame aria-hidden />
          Prepare pictures
        </Button>
        <Button size="sm" variant="ghost" onClick={exportSelection} disabled={busy}>
          <Download aria-hidden />
          Export list
        </Button>

        <Button size="sm" variant="ghost" onClick={clearSelection} aria-label="Clear selection">
          <X aria-hidden />
        </Button>
      </div>

      <AutoAnnotateDialog open={aiOpen} onOpenChange={setAiOpen} uids={annotatableUids} />
    </>
  )
}
